import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatFieldDate } from '../../utils/fieldDates';
import { devFilesApi, dynamicApi, appCfgApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  Save, X, GripVertical, Settings2,
  ChevronDown, ListPlus, Search,
  Link2, SplitSquareHorizontal, ChevronLeft, ChevronRight,
  ChevronUp, ListFilter, RefreshCw, Database, FileDown, DatabaseZap,
  Type, Hash, Calendar, CheckSquare, AlignLeft, AlignCenter, AlignRight, List, Lock, ChevronsLeft,
  Layers, Plus, Trash2, Edit3, TableProperties, Table, LayoutList
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import SourceDatabasePicker, { useSourceDatabase } from '../../components/common/SourceDatabasePicker';

/**
 * ListViewConfigPage - Visual drag-drop form builder for module configs
 * Uses JSON config files that drive the DynamicModule component
 * TODO: Delete this file before production deployment
 */
const ListViewConfigPage = () => {
  // State
  const [tables, setTables] = useState([]);           // All tables from live schema
  const [fullSchema, setFullSchema] = useState([]);   // Full schema with columns for FK lookups
  const [existingConfigs, setExistingConfigs] = useState([]); // Tables that have configs
  const [tablesWithApi, setTablesWithApi] = useState([]); // Tables that have backend API controllers
  const [fieldMetadata, setFieldMetadata] = useState({}); // Field metadata from AppCfg keyed by fieldName
  const [selectedTable, setSelectedTable] = useState('');
  const [moduleConfig, setModuleConfig] = useState(null);  // Full config including schema
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { notify } = useNotifications();
  const [listPanelCollapsed, setListPanelCollapsed] = useState(false);

  // Canvas state - fields placed on canvas
  const [canvasFields, setCanvasFields] = useState([]);

  // Related tables configuration (tabs for child records)
  const [relatedTables, setRelatedTables] = useState([]);
  const [editingRelatedTab, setEditingRelatedTab] = useState(null);
  const [showRelatedTabModal, setShowRelatedTabModal] = useState(false);
  const [selectedRelatedTabIndex, setSelectedRelatedTabIndex] = useState(null);

  // Dual listbox state for column picker
  const [selectedAvailable, setSelectedAvailable] = useState(null);
  const [selectedChosen, setSelectedChosen] = useState(null);

  // Header settings
  const [headerSettings, setHeaderSettings] = useState({
    primaryLine: '',
    secondaryLine: '',
    sortField: '',
    sortDirection: 'ASC'
  });

  // Property editor modal
  const [editingField, setEditingField] = useState(null);

  // Section divider modal
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [sectionTargetRow, setSectionTargetRow] = useState(1);

  // List panel config modal
  const [showListConfigModal, setShowListConfigModal] = useState(false);

  // Derived field modal
  const [showDerivedModal, setShowDerivedModal] = useState(false);
  const [newDerivedSourceField, setNewDerivedSourceField] = useState('');
  const [newDerivedTargetField, setNewDerivedTargetField] = useState('');
  const [newDerivedLabel, setNewDerivedLabel] = useState('');
  const [derivedTargetFields, setDerivedTargetFields] = useState([]);
  const [derivedTargetLoading, setDerivedTargetLoading] = useState(false);

  // No API confirmation modal
  const [showNoApiModal, setShowNoApiModal] = useState(false);
  const [apiTasksHandled, setApiTasksHandled] = useState(false); // Track if we've already prompted for this table

  // Table dropdown legend visibility
  const [showTableLegend, setShowTableLegend] = useState(false);
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const tableDropdownRef = useRef(null);

  // Config tab: 'detail' or 'datasheet'
  const [activeConfigTab, setActiveConfigTab] = useState('detail');

  // Datasheet columns config
  const [datasheetColumns, setDatasheetColumns] = useState([]);
  const [draggedDsColumn, setDraggedDsColumn] = useState(null);
  const draggedDsColumnRef = useRef(null);

  // Datasheet preview data (sample rows from API)
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Column resize state
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Close table dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tableDropdownRef.current && !tableDropdownRef.current.contains(e.target)) {
        setShowTableLegend(false);
      }
    };
    if (showTableLegend) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTableLegend]);

  // Drag state
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverRow, setDragOverRow] = useState(null);
  const [dragOverInsert, setDragOverInsert] = useState(null); // insert-between index

  // Tables, FK targets and preview rows come from the source picker (the template unless another
  // database is picked). Refresh Schema ignores it: the config it writes is shared, so it always
  // reads the template.
  const { databases, source, setSource, ready: sourceReady } = useSourceDatabase();

  // Load available tables from live schema and existing configs
  const loadTables = useCallback(async () => {
    if (!sourceReady) return;
    try {
      setLoading(true);
      // error cleared
      const [schemaRes, configsRes, apiRes] = await Promise.all([
        devFilesApi.getLiveSchema(source || undefined),
        devFilesApi.getModuleConfigs(),
        devFilesApi.getTablesWithApi()
      ]);
      const schemaTables = schemaRes.data?.tables || [];
      const schemaTableNames = schemaTables.map(t => t.name);
      setTables(schemaTableNames.sort());
      setFullSchema(schemaTables); // Store full schema for FK column lookups
      setExistingConfigs(configsRes.data?.configs || []);
      setTablesWithApi(apiRes.data?.tables || []);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load tables');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceReady, source]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // Get FK info for a field from config schema
  const getFieldFKInfo = useCallback((fieldName) => {
    if (!moduleConfig?.schema?.fields) return null;
    const field = moduleConfig.schema.fields.find(f => f.fieldName === fieldName);
    return field?.fkTable || null;
  }, [moduleConfig]);

  // Load or create config for selected table
  const loadTableConfig = useCallback(async (tableName) => {
    if (!tableName) return;

    try {
      setLoading(true);
      // error cleared
      setSuccessMessage(null);

      // Load field metadata for this table from AppCfg
      try {
        const metadataRes = await appCfgApi.getFieldMetadata(tableName);
        // Convert array to object keyed by field name for easy lookup
        const metadataMap = {};
        (metadataRes.data || []).forEach(m => {
          metadataMap[m.field_Name] = m;
        });
        setFieldMetadata(metadataMap);
      } catch (e) {
        // Metadata might not exist yet - that's OK
        setFieldMetadata({});
      }

      // Try to load existing config
      let config = null;
      try {
        const configRes = await devFilesApi.getModuleConfig(tableName);
        config = configRes.data;
      } catch (e) {
        // Config doesn't exist yet - that's OK
      }

      if (config) {
        setModuleConfig(config);
        // Add default labelAlignment to any fields that don't have it
        const fieldsWithDefaults = (config.detailPanel?.fields || []).map(f => ({
          ...f,
          labelAlignment: f.labelAlignment || 'left'
        }));
        setCanvasFields(fieldsWithDefaults);
        setRelatedTables(config.relatedTables || []);
        setDatasheetColumns(config.datasheetPanel?.columns || []);
        setHeaderSettings({
          primaryLine: config.listPanel?.primaryLine || '',
          secondaryLine: config.listPanel?.secondaryLine || '',
          sortField: config.listPanel?.sortField || '',
          sortDirection: config.listPanel?.sortDirection || 'ASC'
        });
      } else {
        // No config yet - initialize empty, user needs to click "Refresh Schema"
        setModuleConfig(null);
        setCanvasFields([]);
        setRelatedTables([]);
        setDatasheetColumns([]);
        setHeaderSettings({
          primaryLine: '',
          secondaryLine: '',
          sortField: '',
          sortDirection: 'ASC'
        });
      }

      setHasChanges(false);
      setSearchTerm('');
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh schema from database (adds new columns, preserves layout)
  const handleRefreshSchema = async () => {
    if (!selectedTable) return;

    try {
      setRefreshing(true);
      // error cleared
      const res = await devFilesApi.refreshModuleConfigSchema(selectedTable);

      // Reload the config
      const configRes = await devFilesApi.getModuleConfig(selectedTable);
      setModuleConfig(configRes.data);

      // Update existing configs list if this was a new one
      if (!existingConfigs.includes(selectedTable)) {
        setExistingConfigs([...existingConfigs, selectedTable].sort());
      }

      // If no layout exists yet, generate a default one
      const existingFields = configRes.data.detailPanel?.fields || [];
      const existingDsCols = configRes.data.datasheetPanel?.columns || [];
      const schemaFields = configRes.data.schema?.fields || [];

      if (existingFields.length === 0 && canvasFields.length === 0) {
        const defaultLayout = generateDefaultLayout(schemaFields);
        setCanvasFields(defaultLayout.fields);
        setHeaderSettings(defaultLayout.headerSettings);
        setSuccessMessage(`Schema loaded. Default layout created with ${defaultLayout.fields.length} fields.`);
        setHasChanges(true); // Mark as needing save
      } else if (canvasFields.length === 0 && existingFields.length > 0) {
        setCanvasFields(existingFields);
        // Also load headerSettings from existing config
        setHeaderSettings({
          primaryLine: configRes.data.listPanel?.primaryLine || '',
          secondaryLine: configRes.data.listPanel?.secondaryLine || '',
          sortField: configRes.data.listPanel?.sortField || '',
          sortDirection: configRes.data.listPanel?.sortDirection || 'ASC'
        });
        setSuccessMessage(res.data.message);
      } else {
        setSuccessMessage(res.data.message);
      }

      // Reconcile datasheet columns against the fresh schema.
      // - Drop stale columns (fieldName no longer in schema — e.g. after rename or delete).
      // - Add new columns that appear in the schema but aren't in the datasheet layout.
      // - Preserve label/width/alignment/visible for columns that still exist.
      if (schemaFields.length > 0) {
        const schemaFieldNames = new Set(schemaFields.map(f => f.fieldName));
        // Source for reconciliation: prefer current in-memory state (may have unsaved edits),
        // fall back to what came from the DB config.
        const source = datasheetColumns.length > 0 ? datasheetColumns : existingDsCols;
        const surviving = source.filter(c => schemaFieldNames.has(c.fieldName));
        const droppedNames = source.filter(c => !schemaFieldNames.has(c.fieldName)).map(c => c.fieldName);
        const existingFieldSet = new Set(surviving.map(c => c.fieldName));
        // Fields in schema that we don't already have a column entry for
        const missing = schemaFields.filter(f => !existingFieldSet.has(f.fieldName) && f.fieldName !== 'ID' && !f.isIdentity);
        const additions = missing.length > 0 ? generateDefaultDatasheetColumns(missing) : [];
        if (source.length === 0 && additions.length > 0) {
          // First-time init — full default layout
          setDatasheetColumns(additions);
          setHasChanges(true);
        } else if (droppedNames.length > 0 || additions.length > 0) {
          setDatasheetColumns([...surviving, ...additions]);
          setHasChanges(true);
          if (droppedNames.length > 0) {
            console.log(`[SVC] Datasheet: dropped stale columns:`, droppedNames);
          }
        }
      }

      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to refresh schema');
    } finally {
      setRefreshing(false);
    }
  };

  // Generate a default layout from schema fields
  const generateDefaultLayout = (schemaFields) => {
    const fields = [];
    let row = 1;
    let colsUsedInRow = 0;
    let order = 1;

    // Find good candidates for header settings
    let primaryLine = '';
    let secondaryLine = '';

    // Filter out ID field
    const layoutFields = schemaFields.filter(f => f.fieldName !== 'ID' && !f.isIdentity);

    layoutFields.forEach((schemaField, idx) => {
      const dataType = schemaField.dataType?.toUpperCase() || '';
      const isFK = !!schemaField.fkTable;

      // Get metadata defaults for this field
      const meta = fieldMetadata[schemaField.fieldName];
      const metaFieldType = meta?.uI_Type;
      const metaFkDisplayField = meta?.fK_Display_Field;

      // Determine column span based on data type
      let colSpan = 8; // default
      if (dataType.includes('TEXT') || dataType.includes('MAX')) {
        colSpan = 24; // full width for large text
      } else if (dataType.includes('NVARCHAR') || dataType.includes('VARCHAR')) {
        // Check length hint in type like NVARCHAR(220)
        const match = dataType.match(/\((\d+)\)/);
        const len = match ? parseInt(match[1]) : 50;
        if (len > 200) colSpan = 16;
        else if (len > 100) colSpan = 12;
        else if (len <= 20) colSpan = 6;
        else colSpan = 8;
      } else if (dataType.includes('INT') || dataType.includes('DECIMAL') || dataType === 'BIT') {
        colSpan = 4;
      } else if (dataType.includes('DATE')) {
        colSpan = 6;
      }

      // Check if field fits on current row
      if (colsUsedInRow + colSpan > 24) {
        row++;
        colsUsedInRow = 0;
        order = 1;
      }

      // Determine field type: use metadata UI_Type if set, otherwise use FK logic or suggest from dataType
      let fieldType = 'text';
      if (metaFieldType && metaFieldType !== 'text') {
        fieldType = metaFieldType;
      } else if (isFK) {
        fieldType = 'dropdown';
      } else {
        fieldType = suggestFieldType(dataType);
      }

      fields.push({
        fieldName: schemaField.fieldName,
        label: formatLabel(schemaField.fieldName),
        row: row,
        order: order,
        colSpan: colSpan,
        labelPosition: 'top',
        fieldType: fieldType,
        fkTable: schemaField.fkTable || null,
        fkDisplayField: metaFkDisplayField || null,
        fkClickable: isFK,
        dataType: schemaField.dataType
      });

      colsUsedInRow += colSpan;
      order++;

      // Pick header settings from first suitable fields
      if (!primaryLine && !isFK && (dataType.includes('CHAR') || dataType.includes('TEXT'))) {
        primaryLine = schemaField.fieldName;
      } else if (!secondaryLine && !isFK && (dataType.includes('CHAR') || dataType.includes('TEXT'))) {
        secondaryLine = schemaField.fieldName;
      }
    });

    return {
      fields,
      headerSettings: {
        primaryLine: primaryLine || layoutFields[0]?.fieldName || '',
        secondaryLine: secondaryLine || '',
        sortField: primaryLine || layoutFields[0]?.fieldName || '',
        sortDirection: 'ASC'
      }
    };
  };

  // Generate default datasheet columns from schema with smart defaults
  // Uses Default_Width from field metadata if available, otherwise falls back to data-type logic
  // Minimum width: 50px for most fields, 40px for boolean (BIT)
  const generateDefaultDatasheetColumns = (schemaFields) => {
    return schemaFields
      .filter(f => f.fieldName !== 'ID' && !f.isIdentity)
      .map((schemaField, idx) => {
        const dataType = schemaField.dataType?.toUpperCase() || '';
        const isBit = dataType === 'BIT';

        // Get metadata defaults for this field
        const meta = fieldMetadata[schemaField.fieldName];
        const metaWidth = meta?.default_Width;

        // Smart defaults for alignment based on data type
        let alignment = 'left';
        if (dataType.includes('INT') || dataType.includes('DECIMAL') || dataType.includes('FLOAT') || dataType.includes('MONEY')) {
          alignment = 'right';
        } else if (isBit || dataType.includes('DATE')) {
          alignment = 'center';
        }

        // Use metadata Default_Width if available, otherwise calculate from data type
        let width = metaWidth || 150;
        if (!metaWidth) {
          if (isBit) {
            width = 50; // Boolean checkbox - can be narrow
          } else if (dataType.includes('INT')) {
            width = 80;
          } else if (dataType.includes('DATE')) {
            width = 120;
          } else if (dataType.includes('TEXT') || dataType.includes('MAX')) {
            width = 300;
          } else if (dataType.includes('NVARCHAR') || dataType.includes('VARCHAR')) {
            const match = dataType.match(/\((\d+)\)/);
            const len = match ? parseInt(match[1]) : 50;
            if (len > 200) width = 250;
            else if (len > 100) width = 180;
            else if (len <= 20) width = 100;
            else width = 150;
          }
        }

        return {
          fieldName: schemaField.fieldName,
          label: formatLabel(schemaField.fieldName),
          width: width,
          visible: idx < 8, // First 8 columns visible by default
          alignment: alignment,
          format: null,
          dataType: dataType // Store for min-width enforcement
        };
      });
  };

  // Get minimum width for a column (40px for BIT, 50px for everything else)
  const getMinWidth = (col) => {
    const dataType = col.dataType?.toUpperCase() || '';
    return dataType === 'BIT' ? 40 : 50;
  };

  // Check if table has API — dedicated controller OR dynamic API (covers all schema tables)
  const tableHasApi = (tableName) => {
    if (!tableName) return false;
    // All tables in the schema have access to the dynamic API (/api/dynamic/{table})
    if (tables.includes(tableName)) return true;
    const tLower = tableName.toLowerCase();
    return tablesWithApi.some(api => {
      const apiLower = api.toLowerCase();
      return apiLower === tLower ||
             apiLower === tLower + 's' ||
             tLower === apiLower + 's' ||
             apiLower === tLower.replace(/_/g, '') ||
             apiLower === tLower.replace(/_/g, '') + 's' ||
             tLower.replace(/_/g, '') === apiLower ||
             tLower.replace(/_/g, '') + 's' === apiLower;
    });
  };

  // Handle table selection
  const handleTableSelect = (tableName) => {
    const proceedWithSelection = () => {
      setSelectedTable(tableName);
      setSuccessMessage(null);
      setApiTasksHandled(false); // Reset task prompt tracking for new table
      if (tableName) {
        loadTableConfig(tableName);
        // Warn if table doesn't have backend API
        if (!tableHasApi(tableName)) {
          notify.warning(`No backend API for ${tableName}. Saving this config will create a task to develop the API.`);
        }
      } else {
        setModuleConfig(null);
        setCanvasFields([]);
        setRelatedTables([]);
      }
    };

    if (hasChanges) {
      notify.confirm('You have unsaved changes. Continue?', proceedWithSelection);
      return;
    }
    proceedWithSelection();
  };

  // Load preview data when table is selected and has API
  useEffect(() => {
    const loadPreviewData = async () => {
      if (!selectedTable || !tableHasApi(selectedTable)) {
        setPreviewData([]);
        return;
      }

      try {
        setLoadingPreview(true);
        const response = await dynamicApi.previewFrom(selectedTable, source);
        // Take first 5 rows for preview
        setPreviewData((response.data || []).slice(0, 5));
      } catch (err) {
        console.warn('Failed to load preview data:', err);
        setPreviewData([]);
      } finally {
        setLoadingPreview(false);
      }
    };

    loadPreviewData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, tablesWithApi, source]);

  // Column resize handlers
  const handleResizeStart = (e, colIndex, currentWidth) => {
    e.preventDefault();
    setResizingColumn(colIndex);
    setResizeStartX(e.clientX);
    setResizeStartWidth(currentWidth);
  };

  useEffect(() => {
    if (resizingColumn === null) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizeStartX;
      const col = datasheetColumns[resizingColumn];
      const minWidth = getMinWidth(col);
      const newWidth = Math.max(minWidth, Math.min(500, resizeStartWidth + delta));
      const updated = [...datasheetColumns];
      updated[resizingColumn] = { ...updated[resizingColumn], width: newWidth };
      setDatasheetColumns(updated);
      setHasChanges(true);
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth, datasheetColumns]);

  // Get available fields (from schema, not yet on canvas)
  const getAvailableFields = () => {
    if (!moduleConfig?.schema?.fields) return [];

    const placedFieldNames = canvasFields
      .filter(f => f.fieldType !== 'section')
      .map(f => f.fieldName);

    let fields = moduleConfig.schema.fields.filter(f =>
      !placedFieldNames.includes(f.fieldName) && f.fieldName !== 'ID' && !f.isIdentity
    );

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      fields = fields.filter(f =>
        f.fieldName.toLowerCase().includes(term) ||
        f.dataType?.toLowerCase().includes(term)
      );
    }

    return fields;
  };

  // Get data type badge color
  const getTypeColor = (dataType) => {
    const type = dataType?.toUpperCase() || '';
    if (type.includes('CHAR') || type.includes('TEXT')) return 'type-string';
    if (type.includes('INT') || type.includes('DECIMAL') || type.includes('NUMERIC') || type.includes('FLOAT')) return 'type-number';
    if (type.includes('DATE') || type.includes('TIME')) return 'type-date';
    if (type === 'BIT') return 'type-boolean';
    return 'type-other';
  };

  // Get short type name
  const getShortType = (dataType) => {
    const type = dataType?.toUpperCase() || '';
    if (type.includes('NVARCHAR') || type.includes('VARCHAR')) return 'STR';
    if (type.includes('INT')) return 'INT';
    if (type.includes('DECIMAL') || type.includes('NUMERIC')) return 'NUM';
    if (type.includes('DATE')) return 'DATE';
    if (type === 'BIT') return 'BOOL';
    return type.split('(')[0]?.substring(0, 4) || '?';
  };

  // Get rows for canvas (organize by row number)
  const getCanvasRows = () => {
    const rows = {};
    canvasFields.forEach(field => {
      const rowNum = field.row || 1;
      if (!rows[rowNum]) rows[rowNum] = [];
      rows[rowNum].push(field);
    });

    Object.keys(rows).forEach(rowNum => {
      rows[rowNum].sort((a, b) => (a.order || 1) - (b.order || 1));
    });

    const maxRow = Math.max(0, ...Object.keys(rows).map(Number));
    if (!rows[maxRow + 1]) rows[maxRow + 1] = [];

    return rows;
  };

  // Drag handlers
  const handlePaletteDragStart = (e, field) => {
    setDraggedField({ ...field, isNew: true });
    e.dataTransfer.setData('text/plain', field.fieldName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCanvasDragStart = (e, field) => {
    setDraggedField({ ...field, isNew: false });
    e.dataTransfer.setData('text/plain', field.fieldName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    // Fallback for environments where onDrop doesn't fire (cross-origin access)
    if (dragOverRow !== null && dragOverRow !== 'palette' && draggedField) {
      // Create a synthetic event for handleDrop
      const syntheticEvent = { preventDefault: () => {}, stopPropagation: () => {} };
      handleDrop(syntheticEvent, dragOverRow);
    } else {
      setDraggedField(null);
      setDragOverRow(null);
    }
  };

  const handleDragOver = (e, rowNum) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverRow(rowNum);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the drop zone, not entering a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverRow(null);
    }
  };

  const handleDrop = (e, targetRow) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverRow(null);

    if (!draggedField) return;

    if (draggedField.isNew) {
      const existingInRow = canvasFields.filter(f => f.row === targetRow);
      const fkTable = draggedField.fkTable || getFieldFKInfo(draggedField.fieldName);

      // Get metadata defaults for this field
      const meta = fieldMetadata[draggedField.fieldName];
      const metaFieldType = meta?.uI_Type;
      const metaFkDisplayField = meta?.fK_Display_Field;

      // Determine field type: use metadata UI_Type if set, otherwise use FK logic or suggest from dataType
      let fieldType = 'text';
      if (metaFieldType && metaFieldType !== 'text') {
        fieldType = metaFieldType;
      } else if (fkTable) {
        fieldType = 'dropdown';
      } else {
        fieldType = suggestFieldType(draggedField.dataType);
      }

      const newField = {
        fieldName: draggedField.fieldName,
        label: formatLabel(draggedField.fieldName),
        row: targetRow,
        order: existingInRow.length + 1,
        colSpan: 8,
        labelPosition: 'top',
        labelAlignment: 'left',
        fieldType: fieldType,
        fkTable: fkTable,
        fkDisplayField: metaFkDisplayField || null,
        fkClickable: fkTable ? true : false,
        dataType: draggedField.dataType
      };
      setCanvasFields([...canvasFields, newField]);
    } else {
      const updatedFields = canvasFields.map(f => {
        if (f.fieldName === draggedField.fieldName) {
          const existingInRow = canvasFields.filter(cf => cf.row === targetRow && cf.fieldName !== f.fieldName);
          return { ...f, row: targetRow, order: existingInRow.length + 1 };
        }
        return f;
      });
      setCanvasFields(updatedFields);
    }

    setDraggedField(null);
    setHasChanges(true);
  };

  // Drop into the gap BETWEEN rows — shifts rows down and inserts at afterRow+1
  const handleDropInsert = (e, afterRow) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverInsert(null);
    setDragOverRow(null);
    if (!draggedField) return;

    const insertAt = afterRow + 1;

    // Shift all existing rows >= insertAt up by 1
    const shifted = canvasFields.map(f =>
      f.row >= insertAt ? { ...f, row: f.row + 1 } : f
    );

    if (draggedField.isNew) {
      const fkTable = draggedField.fkTable || getFieldFKInfo(draggedField.fieldName);
      const meta = fieldMetadata[draggedField.fieldName];
      const metaFieldType = meta?.uI_Type;
      const metaFkDisplayField = meta?.fK_Display_Field;
      let fieldType = 'text';
      if (metaFieldType && metaFieldType !== 'text') fieldType = metaFieldType;
      else if (fkTable) fieldType = 'dropdown';
      else fieldType = suggestFieldType(draggedField.dataType);

      const newField = {
        fieldName: draggedField.fieldName,
        label: formatLabel(draggedField.fieldName),
        row: insertAt,
        order: 1,
        colSpan: 8,
        labelPosition: 'top',
        labelAlignment: 'left',
        fieldType,
        fkTable,
        fkDisplayField: metaFkDisplayField || null,
        fkClickable: fkTable ? true : false,
        dataType: draggedField.dataType
      };
      setCanvasFields([...shifted, newField]);
    } else {
      // Moving existing field — remove from old row, place at insertAt
      const without = shifted.filter(f => f.fieldName !== draggedField.fieldName);
      setCanvasFields([...without, { ...draggedField, row: insertAt, order: 1 }]);
    }

    setDraggedField(null);
    setHasChanges(true);
  };

  const handleRemoveField = (fieldName) => {
    setCanvasFields(canvasFields.filter(f => f.fieldName !== fieldName));
    setHasChanges(true);
  };

  const handleRemoveAll = () => {
    if (canvasFields.length === 0) return;
    notify.confirm('Remove all fields from the layout?', () => {
      setCanvasFields([]);
      setHasChanges(true);
    });
  };

  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return;

    const sectionField = {
      fieldName: `__section_${Date.now()}`,
      label: newSectionTitle,
      row: sectionTargetRow,
      order: 0,
      colSpan: 24,
      labelPosition: 'top',
      fieldType: 'section',
      sectionTitle: newSectionTitle
    };

    const updatedFields = canvasFields.map(f => {
      if (f.row >= sectionTargetRow) {
        return { ...f, row: f.row + 1 };
      }
      return f;
    });

    setCanvasFields([...updatedFields, sectionField]);
    setShowSectionModal(false);
    setNewSectionTitle('');
    setHasChanges(true);
  };

  const handleDerivedSourceChange = async (sourceFieldName) => {
    setNewDerivedSourceField(sourceFieldName);
    setNewDerivedTargetField('');
    setDerivedTargetFields([]);
    if (!sourceFieldName) return;

    const schemaField = (moduleConfig?.schema?.fields || []).find(f => f.fieldName === sourceFieldName);
    const fkTable = schemaField?.fkTable;
    if (!fkTable) return;

    setDerivedTargetLoading(true);
    try {
      const res = await devFilesApi.getTableFields(fkTable, source || undefined);
      setDerivedTargetFields(res.data || []);
    } catch {
      setDerivedTargetFields([]);
    } finally {
      setDerivedTargetLoading(false);
    }
  };

  const handleAddDerivedField = () => {
    if (!newDerivedSourceField || !newDerivedTargetField) return;
    const nextRow = Math.max(0, ...canvasFields.map(f => f.row)) + 1;
    const derivedField = {
      fieldName: `${newDerivedTargetField}_Derived`,
      label: newDerivedLabel || newDerivedTargetField,
      row: nextRow,
      order: 0,
      colSpan: 8,
      labelPosition: 'top',
      labelAlignment: 'left',
      fieldType: 'fk-derived',
      sourceField: newDerivedSourceField,
      targetField: newDerivedTargetField
    };
    setCanvasFields([...canvasFields, derivedField]);
    setShowDerivedModal(false);
    setNewDerivedSourceField('');
    setNewDerivedTargetField('');
    setNewDerivedLabel('');
    setHasChanges(true);
  };

  const handleEditField = (field) => {
    // Ensure defaults for properties that may not exist on older configs
    setEditingField({
      ...field,
      labelPosition: field.labelPosition || 'top',
      labelAlignment: field.labelAlignment || 'left'
    });
  };

  // Move section up or down
  const handleMoveSection = (sectionFieldName, direction) => {
    const section = canvasFields.find(f => f.fieldName === sectionFieldName);
    if (!section) return;

    const currentRow = section.row;
    const targetRow = direction === 'up' ? currentRow - 1 : currentRow + 1;

    if (targetRow < 1) return;

    // Get max row
    const maxRow = Math.max(...canvasFields.map(f => f.row));
    if (direction === 'down' && currentRow >= maxRow) return;

    // Swap rows: move section to target row, move fields at target row to current row
    const updatedFields = canvasFields.map(f => {
      if (f.fieldName === sectionFieldName) {
        return { ...f, row: targetRow };
      }
      if (f.row === targetRow) {
        return { ...f, row: currentRow };
      }
      return f;
    });

    setCanvasFields(updatedFields);
    setHasChanges(true);
  };

  const handleSaveFieldProperties = () => {
    if (!editingField) return;
    setCanvasFields(canvasFields.map(f =>
      f.fieldName === editingField.fieldName ? editingField : f
    ));
    setEditingField(null);
    setHasChanges(true);
  };

  const formatLabel = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const suggestFieldType = (dataType) => {
    const type = dataType?.toUpperCase() || '';
    if (type.includes('DATE')) return 'date';
    if (type === 'BIT') return 'checkbox';
    if (type.includes('INT') || type.includes('DECIMAL') || type.includes('NUMERIC') || type.includes('FLOAT')) return 'number';
    if (type.includes('TEXT') || (type.includes('CHAR') && type.includes('MAX'))) return 'textarea';
    return 'text';
  };

  // Get icon for field type
  const getFieldTypeIcon = (fieldType) => {
    switch (fieldType) {
      case 'checkbox': return <CheckSquare size={11} />;
      case 'dropdown': return <List size={11} />;
      case 'date': return <Calendar size={11} />;
      case 'number': return <Hash size={11} />;
      case 'textarea': return <AlignLeft size={11} />;
      case 'readonly': return <Lock size={11} />;
      case 'spacer': return <AlignCenter size={11} />;
      case 'text':
      default: return <Type size={11} />;
    }
  };

  const handleHeaderChange = (key, value) => {
    setHeaderSettings({ ...headerSettings, [key]: value });
    setHasChanges(true);
  };

  // Check if table has API before saving
  const handleSaveClick = () => {
    if (!selectedTable) return;

    // Check if this table has a backend API
    // Skip the modal if we've already handled the task prompt for this table
    if (!tableHasApi(selectedTable) && !apiTasksHandled) {
      setShowNoApiModal(true);
    } else {
      doSave();
    }
  };

  // Actually perform the save
  const doSave = async (createApiTasks = false) => {
    if (!selectedTable) return;
    try {
      setSaving(true);
      // error cleared

      const config = {
        tableName: selectedTable,
        listPanel: {
          primaryLine: headerSettings.primaryLine || null,
          secondaryLine: headerSettings.secondaryLine || null,
          sortField: headerSettings.sortField || null,
          sortDirection: headerSettings.sortDirection || 'ASC'
        },
        detailPanel: {
          fields: canvasFields
        },
        datasheetPanel: datasheetColumns.length > 0 ? { columns: datasheetColumns } : null,
        relatedTables: relatedTables.length > 0 ? relatedTables : null,
        schema: moduleConfig?.schema || null
      };

      await devFilesApi.saveModuleConfig(selectedTable, config);
      setHasChanges(false);

      // Update existing configs list if this was a new one
      if (!existingConfigs.includes(selectedTable)) {
        setExistingConfigs([...existingConfigs, selectedTable].sort());
      }

      // If we need to create API tasks, do that now
      if (createApiTasks) {
        await createApiSetupTasks();
        setSuccessMessage('Config saved. API setup tasks created.');
      } else {
        setSuccessMessage('Config saved successfully');
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save config');
    } finally {
      setSaving(false);
      setShowNoApiModal(false);
    }
  };

  // Create sprint tasks for API setup (JSON format)
  const createApiSetupTasks = async () => {
    try {
      await devFilesApi.createTask({
        task: `Create API: ${selectedTable}`,
        status: 'pending',
        details: `Create CRUD API controller for ${selectedTable} table.\n- GET /api/${selectedTable.toLowerCase()}s (all)\n- GET /api/${selectedTable.toLowerCase()}s/{id}\n- POST /api/${selectedTable.toLowerCase()}s\n- PUT /api/${selectedTable.toLowerCase()}s/{id}\n- DELETE /api/${selectedTable.toLowerCase()}s/{id}`,
        source: 'Standard View Composer',
      });
      await devFilesApi.createTask({
        task: `Test API: ${selectedTable}`,
        status: 'pending',
        details: `Test all CRUD endpoints for ${selectedTable}.\nVerify FK relationships return display values.`,
        source: 'Standard View Composer',
      });
      await devFilesApi.createTask({
        task: `Build Module: ${selectedTable}`,
        status: 'pending',
        details: `Build list view module using config: module-configs/${selectedTable}.json`,
        source: 'Standard View Composer',
      });
    } catch (err) {
      console.error('Failed to create API tasks:', err);
    }
  };

  const handleCreateTask = async () => {
    if (!selectedTable || canvasFields.length === 0) return;

    const rows = getCanvasRows();
    let fieldLayout = '';
    Object.keys(rows).sort((a, b) => a - b).forEach(rowNum => {
      const rowFields = rows[rowNum];
      if (rowFields.length > 0) {
        const rowContent = rowFields.map(f => {
          if (f.fieldType === 'section') return `[SECTION: ${f.sectionTitle}]`;
          return `${f.label} (${f.colSpan}col)`;
        }).join(' -> ');
        fieldLayout += `Row ${rowNum}: ${rowContent}\n`;
      }
    });

    const fkFields = canvasFields.filter(f => f.fkTable);
    let fkSection = '';
    if (fkFields.length > 0) {
      fkSection = '\n\nFK Relationships:\n' + fkFields.map(f =>
        `- ${f.fieldName} -> ${f.fkTable}.${f.fkDisplayField || 'ID'}`
      ).join('\n');
    }

    const taskDetails = `Use listview template: module-templates/${selectedTable}-listview.csv

Header Settings:
- PrimaryLine: ${headerSettings.primaryLine || '(not set)'}
- SecondaryLine: ${headerSettings.secondaryLine || '(not set)'}
- SortField: ${headerSettings.sortField || '(not set)'}
- SortDirection: ${headerSettings.sortDirection}

Field Layout:
${fieldLayout}${fkSection}`;

    try {
      setSaving(true);
      await devFilesApi.createTask({
        task: `Build Module: ${selectedTable}`,
        status: 'pending',
        details: taskDetails,
        source: 'Standard View Composer',
      });
      alert(`Task created: Build Module: ${selectedTable}`);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadExisting = () => {
    if (selectedTable) loadTableConfig(selectedTable);
  };

  // Sync field metadata from field manager
  const handleSyncFieldMetadata = async () => {
    if (!selectedTable) return;
    try {
      const metadataRes = await appCfgApi.getFieldMetadata(selectedTable);
      const metadataMap = {};
      (metadataRes.data || []).forEach(m => {
        metadataMap[m.field_Name] = m;
      });
      setFieldMetadata(metadataMap);

      // Update datasheet column widths from field metadata
      if (datasheetColumns.length > 0) {
        const updatedColumns = datasheetColumns.map(col => {
          const meta = metadataMap[col.fieldName];
          if (meta?.default_Width) {
            return { ...col, width: meta.default_Width };
          }
          return col;
        });
        setDatasheetColumns(updatedColumns);
        setHasChanges(true);
      }

      notify.success('Field properties synced from Field Manager');
    } catch (err) {
      notify.error('Failed to sync field properties');
    }
  };

  const getAllFieldNames = () => {
    return moduleConfig?.schema?.fields?.map(f => f.fieldName).filter(n => n !== 'ID') || [];
  };

  const getFieldDataType = (fieldName) => {
    const field = moduleConfig?.schema?.fields?.find(f => f.fieldName === fieldName);
    return field?.dataType || null;
  };

  // Get columns for a table from full schema (for FK display field dropdown)
  const getTableColumns = (tableName) => {
    if (!tableName || !fullSchema.length) return [];
    const table = fullSchema.find(t => t.name === tableName);
    return table?.columns?.map(c => c.name).filter(n => n !== 'ID') || [];
  };

  // Find tables that have FK columns pointing to the current table (candidates for related tables)
  const getCandidateRelatedTables = () => {
    if (!selectedTable || !fullSchema.length) return [];
    const candidates = [];

    fullSchema.forEach(table => {
      if (table.name === selectedTable) return; // Skip self

      table.columns?.forEach(col => {
        // Check if column is FK pointing to current table (schema uses foreignKeyTable)
        const fkTarget = col.foreignKeyTable || col.fkTable;
        if (fkTarget === selectedTable) {
          candidates.push({
            tableName: table.name,
            fkField: col.name,
            columns: table.columns?.map(c => c.name).filter(n => n !== 'ID' && n !== col.name) || []
          });
        }
      });
    });

    return candidates;
  };

  // Add a new related table tab
  const handleAddRelatedTab = () => {
    console.log('handleAddRelatedTab called');
    const candidates = getCandidateRelatedTables();
    console.log('Candidates:', candidates);
    if (candidates.length === 0) {
      notify.warning('No tables found with FK relationships to this table.');
      return;
    }

    // Create a new empty tab for editing
    setEditingRelatedTab({
      tableName: '',
      label: '',
      fkField: '',
      columns: [],
      sortField: '',
      sortDirection: 'ASC',
      isNew: true
    });
    setSelectedAvailable(null);
    setSelectedChosen(null);
    setShowRelatedTabModal(true);
    console.log('Modal should open now');
  };

  // Edit existing related tab
  const handleEditRelatedTab = (tab, index) => {
    setEditingRelatedTab({ ...tab, index, isNew: false });
    setSelectedAvailable(null);
    setSelectedChosen(null);
    setShowRelatedTabModal(true);
  };

  // Remove related tab
  const handleRemoveRelatedTab = (index) => {
    notify.confirm(`Remove "${relatedTables[index]?.label || 'this tab'}" from related tables?`, () => {
      const updated = [...relatedTables];
      updated.splice(index, 1);
      setRelatedTables(updated);
      setHasChanges(true);
    });
  };

  // Move related tab left/right
  const handleMoveRelatedTab = (index, direction) => {
    if (index === null) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= relatedTables.length) return;
    const updated = [...relatedTables];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setRelatedTables(updated);
    setSelectedRelatedTabIndex(newIndex); // Follow the moved tab
    setHasChanges(true);
  };

  // Save related tab from modal
  const handleSaveRelatedTab = () => {
    if (!editingRelatedTab) return;

    // Convert column names to objects with fieldName/label for backend DTO
    // Handle both string (newly added) and object (loaded from config) formats
    const columnsFormatted = (editingRelatedTab.columns || []).map(col => {
      if (typeof col === 'string') {
        return { fieldName: col, label: formatLabel(col) };
      }
      return col; // Already an object
    });

    const tabData = {
      tableName: editingRelatedTab.tableName,
      label: editingRelatedTab.label || formatLabel(editingRelatedTab.tableName),
      fkField: editingRelatedTab.fkField,
      columns: columnsFormatted,
      sortField: editingRelatedTab.sortField || null,
      sortDirection: editingRelatedTab.sortDirection || 'ASC'
    };

    if (editingRelatedTab.isNew) {
      setRelatedTables([...relatedTables, tabData]);
    } else {
      const updated = [...relatedTables];
      updated[editingRelatedTab.index] = tabData;
      setRelatedTables(updated);
    }

    setShowRelatedTabModal(false);
    setEditingRelatedTab(null);
    setHasChanges(true);
  };

  // When table changes in related tab editor, update FK field and reset columns
  const handleRelatedTabTableChange = (tableName) => {
    const candidates = getCandidateRelatedTables();
    const candidate = candidates.find(c => c.tableName === tableName);

    setEditingRelatedTab({
      ...editingRelatedTab,
      tableName,
      fkField: candidate?.fkField || '',
      columns: [],
      label: formatLabel(tableName)
    });
  };

  // Dual listbox handlers for column picker
  const handleAddColumn = () => {
    if (!editingRelatedTab || !selectedAvailable) return;
    const cols = [...(editingRelatedTab.columns || [])];
    cols.push(selectedAvailable);
    setEditingRelatedTab({ ...editingRelatedTab, columns: cols });
    setSelectedAvailable(null);
  };

  const handleRemoveColumn = () => {
    if (!editingRelatedTab || !selectedChosen) return;
    const cols = [...(editingRelatedTab.columns || [])];
    const idx = cols.findIndex(c => (typeof c === 'string' ? c : c.fieldName) === selectedChosen);
    if (idx >= 0) {
      cols.splice(idx, 1);
    }
    setEditingRelatedTab({ ...editingRelatedTab, columns: cols });
    setSelectedChosen(null);
  };

  const handleMoveColumnUp = () => {
    if (!editingRelatedTab || !selectedChosen) return;
    const cols = [...(editingRelatedTab.columns || [])];
    const idx = cols.findIndex(c => (typeof c === 'string' ? c : c.fieldName) === selectedChosen);
    if (idx > 0) {
      [cols[idx - 1], cols[idx]] = [cols[idx], cols[idx - 1]];
      setEditingRelatedTab({ ...editingRelatedTab, columns: cols });
    }
  };

  const handleMoveColumnDown = () => {
    if (!editingRelatedTab || !selectedChosen) return;
    const cols = [...(editingRelatedTab.columns || [])];
    const idx = cols.findIndex(c => (typeof c === 'string' ? c : c.fieldName) === selectedChosen);
    if (idx >= 0 && idx < cols.length - 1) {
      [cols[idx], cols[idx + 1]] = [cols[idx + 1], cols[idx]];
      setEditingRelatedTab({ ...editingRelatedTab, columns: cols });
    }
  };

  // Get column name from column object or string
  const getColName = (col) => typeof col === 'string' ? col : col.fieldName;

  const hasConfig = existingConfigs.includes(selectedTable);
  const hasSchema = moduleConfig?.schema?.fields?.length > 0;

  // Check if API exists (flexible singular/plural matching)
  const hasApi = selectedTable ? tablesWithApi.some(api => {
    const tLower = selectedTable.toLowerCase();
    const apiLower = api.toLowerCase();
    return apiLower === tLower ||
           apiLower === tLower + 's' ||
           apiLower + 's' === tLower ||
           apiLower.replace(/_/g, '') === tLower.replace(/_/g, '') ||
           apiLower.replace(/_/g, '') === tLower.replace(/_/g, '') + 's' ||
           apiLower.replace(/_/g, '') + 's' === tLower.replace(/_/g, '');
  }) : false;

  if (loading && tables.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="module-container standard-view-config-module">
      {/* Header Bar */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h2>Standard View Composer</h2>
          <div className="lvc-table-dropdown-wrapper">
            <label className="stui-composer-select-label">Table</label>
            <div className="stui-composer-select" ref={tableDropdownRef}>
              <button
                type="button"
                className="stui-composer-select-trigger"
                onClick={() => !loading && setShowTableLegend(!showTableLegend)}
                disabled={loading}
              >
                <span>{selectedTable || '-- Select --'}</span>
                <ChevronDown size={16} />
              </button>
              {showTableLegend && (
                <div className="stui-composer-select-panel">
                  <div className="stui-composer-select-search">
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Search tables..."
                      value={tableSearchTerm}
                      onChange={(e) => setTableSearchTerm(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="stui-composer-select-options">
                    {!tableSearchTerm && (
                      <div
                        className={`stui-composer-select-option ${!selectedTable ? 'selected' : ''}`}
                        onClick={() => { handleTableSelect(''); setShowTableLegend(false); setTableSearchTerm(''); }}
                      >
                        -- Select --
                      </div>
                    )}
                    {tables
                      .filter(t => !tableSearchTerm || t.toLowerCase().includes(tableSearchTerm.toLowerCase()))
                      .map(t => {
                        // Check API match with flexible singular/plural comparison
                        const tLower = t.toLowerCase();
                        const hasApi = tablesWithApi.some(api => {
                          const apiLower = api.toLowerCase();
                          return apiLower === tLower ||
                                 apiLower === tLower + 's' ||
                                 apiLower + 's' === tLower ||
                                 apiLower.replace(/_/g, '') === tLower.replace(/_/g, '') ||
                                 apiLower.replace(/_/g, '') === tLower.replace(/_/g, '') + 's' ||
                                 apiLower.replace(/_/g, '') + 's' === tLower.replace(/_/g, '');
                        });
                        const hasFile = existingConfigs.includes(t);
                        return (
                          <div
                            key={t}
                            className={`stui-composer-select-option ${selectedTable === t ? 'selected' : ''}`}
                            onClick={() => { handleTableSelect(t); setShowTableLegend(false); setTableSearchTerm(''); }}
                          >
                            <span className="stui-composer-select-option-name">{t}</span>
                            <span className="stui-composer-select-option-icons">
                              {hasFile && <span title="Config record exists in AppCfg">📄</span>}
                              {hasApi && <span title="Backend API exists">⚡</span>}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                  <div className="stui-composer-select-legend">
                    <span>📄 AppCfg Record</span>
                    <span>⚡ Backend API</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {selectedTable && (
            <>
              <button
                className="stui-crud-btn"
                onClick={handleRefreshSchema}
                disabled={loading || refreshing}
                title={hasSchema ? "Refresh schema from the template database to detect new columns" : "Initialize config from the template database schema"}
              >
                {refreshing ? <RefreshCw size={18} className="stui-spinning" /> : <Database size={18} />}
              </button>
              {hasConfig && (
                <button
                  className="stui-crud-btn"
                  onClick={handleLoadExisting}
                  disabled={loading}
                  title="Reload saved config from file"
                >
                  <FileDown size={18} />
                </button>
              )}
              {hasSchema && (
                <button
                  className="stui-crud-btn"
                  onClick={handleSyncFieldMetadata}
                  disabled={loading}
                  title="Sync field properties from Field Manager"
                >
                  <DatabaseZap size={18} />
                </button>
              )}
            </>
          )}
        </div>
        <div className="stui-module-header-actions">
          <SourceDatabasePicker databases={databases} value={source} onChange={setSource} />
          {successMessage && <span className="success-indicator">{successMessage}</span>}
          {hasChanges && <span className="unsaved-indicator">Unsaved</span>}
          <button
            className={`stui-crud-btn ${!selectedTable || !hasSchema ? 'disabled' : ''}`}
            onClick={handleSaveClick}
            disabled={saving || !selectedTable || !hasSchema}
            title="Save config to AppCfg"
          >
            <Save size={18} />
          </button>
          <button
            className={`stui-crud-btn ${!selectedTable || canvasFields.length === 0 || hasChanges || (hasConfig && hasApi) ? 'disabled' : ''}`}
            onClick={handleCreateTask}
            disabled={saving || !selectedTable || canvasFields.length === 0 || hasChanges || (hasConfig && hasApi)}
            title={hasConfig && hasApi ? "Config and API exist - just save to update" : "Add module build task to Task Log"}
          >
            <ListPlus size={18} />
          </button>
        </div>
      </div>

      {/* Empty state when table selected but no schema */}
      {selectedTable && !hasSchema && !loading && (
        <div className="lvc-empty-state">
          <Database size={48} className="empty-state-icon" />
          <h3>No Configuration Found</h3>
          <p>Click <strong>Init from DB</strong> to create a config from the database schema for {selectedTable}.</p>
        </div>
      )}

      {/* Config Tab Bar - Detail View vs Datasheet */}
      {selectedTable && hasSchema && (
        <div className="lvc-config-tabs">
          <button
            className={`lvc-config-tab ${activeConfigTab === 'detail' ? 'active' : ''}`}
            onClick={() => setActiveConfigTab('detail')}
          >
            <LayoutList size={16} />
            <span>List View</span>
          </button>
          <button
            className={`lvc-config-tab ${activeConfigTab === 'datasheet' ? 'active' : ''}`}
            onClick={() => {
              setActiveConfigTab('datasheet');
              // Auto-generate columns if empty but schema exists
              if (datasheetColumns.length === 0 && moduleConfig?.schema?.fields?.length > 0) {
                const defaultCols = generateDefaultDatasheetColumns(moduleConfig.schema.fields);
                setDatasheetColumns(defaultCols);
                setHasChanges(true);
              }
            }}
          >
            <Table size={16} />
            <span>Datasheet</span>
          </button>
        </div>
      )}

      {selectedTable && hasSchema && activeConfigTab === 'detail' && (
        <>
        {/* Detail Panel Layout - Full Width Section Header */}
        <div className="lvc-full-section-header">
          <span>Detail Panel Layout</span>
          <div className="lvc-section-header-controls">
            <button
              className="lvc-section-btn"
              onClick={() => { setSectionTargetRow(Math.max(1, ...canvasFields.map(f => f.row), 0) + 1); setShowSectionModal(true); }}
              title="Insert section divider"
            >
              <SplitSquareHorizontal size={16} style={{ transform: 'rotate(90deg)' }} />
            </button>
            <button
              className="lvc-section-btn"
              onClick={() => setShowDerivedModal(true)}
              title="Add derived field (read-only value from related record)"
            >
              <Plus size={16} />
            </button>
            <button
              className="lvc-section-btn"
              onClick={() => {
                const nextRow = Math.max(0, ...canvasFields.map(f => f.row)) + 1;
                const spacer = {
                  fieldName: `__spacer_${Date.now()}`,
                  label: 'Spacer',
                  row: nextRow,
                  order: 0,
                  colSpan: 4,
                  labelPosition: 'top',
                  labelAlignment: 'left',
                  fieldType: 'spacer'
                };
                setCanvasFields([...canvasFields, spacer]);
                setHasChanges(true);
              }}
              title="Add spacer block (empty column space)"
            >
              <AlignCenter size={16} />
            </button>
            <button
              className="lvc-section-btn danger"
              onClick={handleRemoveAll}
              disabled={canvasFields.length === 0}
              title="Remove all fields from layout"
            >
              <ChevronsLeft size={16} />
            </button>
          </div>
        </div>

        <div className={`list-view ${listPanelCollapsed ? 'list-collapsed' : ''}`}>
          {/* Available Fields Panel (like ListPanel) */}
          {listPanelCollapsed ? (
            <div className="list-panel-collapsed">
              <button onClick={() => setListPanelCollapsed(false)} className="list-panel-expand-btn" title="Expand">
                <ChevronRight size={16} />
              </button>
            </div>
          ) : (
            <div
              className={`list-panel lvc-fields-panel ${dragOverRow === 'palette' ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverRow('palette'); }}
              onDragLeave={() => setDragOverRow(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverRow(null);
                if (draggedField && !draggedField.isNew) {
                  handleRemoveField(draggedField.fieldName);
                  setDraggedField(null);
                }
              }}
            >
              <div className="list-panel-header">
                <button
                  className="lvc-section-btn"
                  onClick={() => setShowListConfigModal(true)}
                  title="List View Search Configuration"
                >
                  <ListFilter size={16} />
                </button>
                <span className="panel-title">Available Fields ({getAvailableFields().length})</span>
                <button onClick={() => setListPanelCollapsed(true)} className="list-panel-collapse-btn" title="Collapse">
                  <ChevronLeft size={16} />
                </button>
              </div>
              {moduleConfig?.schema && (
                <div className="lvc-panel-meta">
                  {canvasFields.filter(f => f.fieldType !== 'section').length} / {moduleConfig.schema.fields?.filter(f => !f.isIdentity).length || 0} placed
                </div>
              )}

              <div className="lvc-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')}><X size={14} /></button>
                )}
              </div>

              <div className="list-scroll">
                {getAvailableFields().map(field => {
                  const fkTable = field.fkTable || getFieldFKInfo(field.fieldName);
                  return (
                    <div
                      key={field.fieldName}
                      className="lvc-field-item"
                      draggable="true"
                      onDragStart={(e) => handlePaletteDragStart(e, field)}
                      onDragEnd={handleDragEnd}
                    >
                      <GripVertical size={12} className="drag-handle" />
                      <span className="field-name">{field.fieldName}</span>
                      <div className="field-badges">
                        {fkTable && (
                          <span className="fk-badge" title={`FK → ${fkTable}`}>
                            <Link2 size={9} /> {fkTable}
                          </span>
                        )}
                        <span className={`type-badge ${getTypeColor(field.dataType)}`}>
                          {getShortType(field.dataType)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {getAvailableFields().length === 0 && (
                  <div className="lvc-empty">{searchTerm ? 'No matches' : 'All fields placed'}</div>
                )}
              </div>
            </div>
          )}

          {/* Form Builder Panel (like detail-panel) */}
          <div className="detail-panel lvc-canvas-panel">
            {/* Form Canvas - Uses actual detail-grid-24 */}
            <div className="lvc-form-canvas">
              {Object.entries(getCanvasRows()).sort((a, b) => Number(a[0]) - Number(b[0])).map(([rowNum, fields], idx, arr) => (
                <React.Fragment key={rowNum}>
                  {/* Insert strip BEFORE first row */}
                  {idx === 0 && (
                    <div
                      className={`lvc-insert-strip ${dragOverInsert === 0 ? 'active' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverInsert(0); setDragOverRow(null); }}
                      onDragLeave={() => setDragOverInsert(null)}
                      onDrop={(e) => handleDropInsert(e, 0)}
                    />
                  )}
                <div
                  className={`lvc-canvas-row ${dragOverRow === Number(rowNum) ? 'drag-over' : ''}`}
                  onDragOver={(e) => handleDragOver(e, Number(rowNum))}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, Number(rowNum))}
                >
                  <span className="row-label">R{rowNum}</span>
                  <div
                    className="detail-grid-24"
                    onDragOver={(e) => handleDragOver(e, Number(rowNum))}
                    onDrop={(e) => handleDrop(e, Number(rowNum))}
                  >
                    {fields.length === 0 ? (
                      <div
                        className="col-span-24 lvc-drop-hint"
                        onDragOver={(e) => handleDragOver(e, Number(rowNum))}
                        onDrop={(e) => handleDrop(e, Number(rowNum))}
                      >Drop fields here</div>
                    ) : (
                      fields.map(field => {
                        const dataType = field.dataType || getFieldDataType(field.fieldName);
                        const isSection = field.fieldType === 'section';

                        if (isSection) {
                          const maxRow = Math.max(...canvasFields.map(f => f.row));
                          return (
                            <div key={field.fieldName} className="col-span-24">
                              <div className="detail-section-header lvc-section">
                                <span className="section-title">{field.sectionTitle}</span>
                                <div className="section-controls">
                                  <button
                                    className="lvc-section-btn"
                                    onClick={() => handleMoveSection(field.fieldName, 'up')}
                                    disabled={field.row <= 1}
                                    title="Move Up"
                                  >
                                    <ChevronUp size={14} />
                                  </button>
                                  <button
                                    className="lvc-section-btn"
                                    onClick={() => handleMoveSection(field.fieldName, 'down')}
                                    disabled={field.row >= maxRow}
                                    title="Move Down"
                                  >
                                    <ChevronDown size={14} />
                                  </button>
                                  <button className="lvc-section-btn delete" onClick={() => handleRemoveField(field.fieldName)} title="Remove">
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={field.fieldName}
                            className={`col-span-${field.colSpan} lvc-placed-field label-${field.labelPosition || 'top'}`}
                            draggable="true"
                            onDragStart={(e) => handleCanvasDragStart(e, field)}
                            onDragEnd={handleDragEnd}
                            onDoubleClick={() => handleEditField(field)}
                          >
                            <div className="lvc-tile-label">
                              <GripVertical size={10} className="drag-handle" />
                              <span className="label-text">{field.label}</span>
                            </div>
                            <div className="lvc-tile-input">
                              <div className="input-icons">
                                {fieldMetadata[field.fieldName]?.fK_Table && (
                                  <span className="fk-badge" title={`FK → ${fieldMetadata[field.fieldName].fK_Table}${fieldMetadata[field.fieldName]?.fK_Clickable !== false ? ' (clickable)' : ''}`}>
                                    <Link2 size={9} /> {fieldMetadata[field.fieldName].fK_Table}
                                  </span>
                                )}
                                <span className={`field-type-badge ${field.fieldType || 'text'}`} title={field.fieldType || 'text'}>
                                  {getFieldTypeIcon(field.fieldType)}
                                </span>
                                <span className={`type-badge ${getTypeColor(dataType)}`}>{getShortType(dataType)}</span>
                              </div>
                              <span className="colspan-indicator">{field.colSpan}</span>
                            </div>
                            <div className="lvc-tile-controls">
                              <button onClick={(e) => { e.stopPropagation(); handleEditField(field); }} title="Edit"><Settings2 size={12} /></button>
                              <button onClick={(e) => { e.stopPropagation(); handleRemoveField(field.fieldName); }} title="Remove"><X size={12} /></button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                {/* Insert strip AFTER this row */}
                <div
                  className={`lvc-insert-strip ${dragOverInsert === Number(rowNum) ? 'active' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverInsert(Number(rowNum)); setDragOverRow(null); }}
                  onDragLeave={() => setDragOverInsert(null)}
                  onDrop={(e) => handleDropInsert(e, Number(rowNum))}
                />
                </React.Fragment>
              ))}
            </div>

          </div>
        </div>

        {/* Related Information - Full Width Section Header */}
        <div className="lvc-full-section-header">
          <Layers size={18} />
          <span>Related Information</span>
          <span className="lvc-related-count">({relatedTables.length} tabs)</span>
          <div className="lvc-related-header-controls">
            <button
              className="lvc-order-btn"
              onClick={() => handleMoveRelatedTab(selectedRelatedTabIndex, 'up')}
              disabled={selectedRelatedTabIndex === null || selectedRelatedTabIndex === 0}
              title="Move selected tab left"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className="lvc-order-btn"
              onClick={() => handleMoveRelatedTab(selectedRelatedTabIndex, 'down')}
              disabled={selectedRelatedTabIndex === null || selectedRelatedTabIndex === relatedTables.length - 1}
              title="Move selected tab right"
            >
              <ChevronRight size={16} />
            </button>
            <button
              className="lvc-add-circle-btn"
              onClick={() => { handleAddRelatedTab(); }}
              title="Add related table tab"
            >
              <TableProperties size={16} />
              <Plus size={10} className="plus-overlay" />
            </button>
          </div>
        </div>

        <div className="lvc-related-content">
          {relatedTables.length === 0 ? (
            <div className="lvc-related-empty">
              <p>No related table tabs configured.</p>
              <p className="hint">Add tabs to show child records from tables that reference {selectedTable}.</p>
            </div>
          ) : (
            <div className="lvc-related-tabs-horizontal">
              {relatedTables.map((tab, idx) => (
                <div
                  key={idx}
                  className={`lvc-related-tab-h ${selectedRelatedTabIndex === idx ? 'selected' : ''}`}
                  onClick={() => setSelectedRelatedTabIndex(idx)}
                >
                  <div className="tab-info-h">
                    <span className="tab-label">{tab.label}</span>
                    <span className="tab-meta">{tab.columns?.length || 0} cols</span>
                  </div>
                  <div className="tab-actions-h">
                    <button onClick={(e) => { e.stopPropagation(); handleEditRelatedTab(tab, idx); }} title="Edit">
                      <Edit3 size={12} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleRemoveRelatedTab(idx); }} title="Remove" className="delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
      )}

      {/* Datasheet Config Tab */}
      {selectedTable && hasSchema && activeConfigTab === 'datasheet' && (
        <div className="lvc-datasheet-config">
          <div className="lvc-full-section-header">
            <span>Datasheet Columns</span>
            <span className="lvc-related-count">({datasheetColumns.filter(c => c.visible).length} visible)</span>
          </div>

          <div className="lvc-datasheet-list">
            <div className="lvc-ds-header">
              <span className="ds-col-drag"></span>
              <span className="ds-col-visible">Show</span>
              <span className="ds-col-field">Field</span>
              <span className="ds-col-label">Label</span>
              <span className="ds-col-width">Width</span>
              <span className="ds-col-align">Align</span>
            </div>

            {datasheetColumns.map((col, idx) => (
              <div
                key={col.fieldName}
                className={`lvc-ds-row ${draggedDsColumn === idx ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => {
                  setDraggedDsColumn(idx);
                  draggedDsColumnRef.current = idx;
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  const fromIdx = draggedDsColumnRef.current;
                  if (fromIdx !== null && fromIdx !== idx) {
                    const updated = [...datasheetColumns];
                    const [moved] = updated.splice(fromIdx, 1);
                    updated.splice(idx, 0, moved);
                    setDatasheetColumns(updated);
                    setDraggedDsColumn(idx);
                    draggedDsColumnRef.current = idx;
                    setHasChanges(true);
                  }
                }}
                onDragEnd={() => {
                  setDraggedDsColumn(null);
                  draggedDsColumnRef.current = null;
                }}
              >
                <span className="ds-col-drag">
                  <GripVertical size={14} />
                </span>
                <span className="ds-col-visible">
                  <input
                    type="checkbox"
                    className="stui-checkbox-input"
                    checked={col.visible}
                    onChange={(e) => {
                      const updated = [...datasheetColumns];
                      updated[idx] = { ...col, visible: e.target.checked };
                      setDatasheetColumns(updated);
                      setHasChanges(true);
                    }}
                  />
                </span>
                <span className="ds-col-field" title={col.fieldName}>
                  {col.fieldName}
                </span>
                <span className="ds-col-label">
                  <input
                    type="text"
                    className="ds-input"
                    value={col.label}
                    onChange={(e) => {
                      const updated = [...datasheetColumns];
                      updated[idx] = { ...col, label: e.target.value };
                      setDatasheetColumns(updated);
                      setHasChanges(true);
                    }}
                  />
                </span>
                <span className="ds-col-width">
                  <input
                    type="number"
                    step={20}
                    className={`ds-input ds-input-sm ${col.width < getMinWidth(col) ? 'ds-input-warning' : ''}`}
                    min={getMinWidth(col)}
                    max="500"
                    value={col.width}
                    onChange={(e) => {
                      const updated = [...datasheetColumns];
                      const newWidth = parseInt(e.target.value) || 0;
                      updated[idx] = { ...col, width: newWidth };
                      setDatasheetColumns(updated);
                      setHasChanges(true);
                    }}
                    onBlur={(e) => {
                      const minWidth = getMinWidth(col);
                      const value = parseInt(e.target.value) || minWidth;
                      if (value < minWidth) {
                        notify.warning(`Minimum width is ${minWidth}px for this column type`);
                        const updated = [...datasheetColumns];
                        updated[idx] = { ...col, width: minWidth };
                        setDatasheetColumns(updated);
                      } else if (value > 500) {
                        const updated = [...datasheetColumns];
                        updated[idx] = { ...col, width: 500 };
                        setDatasheetColumns(updated);
                      }
                    }}
                  />
                </span>
                <span className="ds-col-align">
                  <button
                    className={`ds-align-btn ${col.alignment === 'left' ? 'active' : ''}`}
                    onClick={() => {
                      const updated = [...datasheetColumns];
                      updated[idx] = { ...col, alignment: 'left' };
                      setDatasheetColumns(updated);
                      setHasChanges(true);
                    }}
                    title="Left"
                  >
                    <AlignLeft size={14} />
                  </button>
                  <button
                    className={`ds-align-btn ${col.alignment === 'center' ? 'active' : ''}`}
                    onClick={() => {
                      const updated = [...datasheetColumns];
                      updated[idx] = { ...col, alignment: 'center' };
                      setDatasheetColumns(updated);
                      setHasChanges(true);
                    }}
                    title="Center"
                  >
                    <AlignCenter size={14} />
                  </button>
                  <button
                    className={`ds-align-btn ${col.alignment === 'right' ? 'active' : ''}`}
                    onClick={() => {
                      const updated = [...datasheetColumns];
                      updated[idx] = { ...col, alignment: 'right' };
                      setDatasheetColumns(updated);
                      setHasChanges(true);
                    }}
                    title="Right"
                  >
                    <AlignRight size={14} />
                  </button>
                </span>
              </div>
            ))}

            {datasheetColumns.length === 0 && (
              <div className="lvc-ds-empty">
                No columns configured. Click "Init from DB" to generate default columns.
              </div>
            )}
          </div>

          {/* Live Preview Section */}
          {datasheetColumns.length > 0 && (
            <>
              <div className="lvc-full-section-header stui-mt-md">
                <span>Live Preview</span>
                {!tableHasApi(selectedTable) && (
                  <span className="lvc-preview-note">(No API - headers only)</span>
                )}
                {loadingPreview && <span className="lvc-preview-note">Loading...</span>}
              </div>

              <div className="lvc-ds-preview-wrapper">
                <div className="lvc-ds-preview">
                  <table
                    className="lvc-ds-preview-table"
                    style={{
                      tableLayout: 'fixed',
                      width: datasheetColumns.filter(c => c.visible !== false).reduce((sum, c) => sum + (c.width || 150), 0) + 'px'
                    }}
                  >
                    <colgroup>
                      {datasheetColumns.filter(c => c.visible !== false).map(col => (
                        <col key={col.fieldName} style={{ width: `${col.width || 150}px` }} />
                      ))}
                    </colgroup>
                    <thead>
                      <tr>
                        {datasheetColumns.filter(c => c.visible !== false).map((col, idx) => {
                          const actualIdx = datasheetColumns.findIndex(c => c.fieldName === col.fieldName);
                          const meta = fieldMetadata[col.fieldName];
                          const isVertical = meta?.header_Vertical;
                          return (
                            <th
                              key={col.fieldName}
                              className={isVertical ? 'vertical-header' : ''}
                              style={{ textAlign: col.alignment || 'left' }}
                            >
                              <span>{col.label}</span>
                              <div
                                className="lvc-resize-handle"
                                onMouseDown={(e) => handleResizeStart(e, actualIdx, col.width || 150)}
                              />
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.length > 0 ? (
                        previewData.map((row, rowIdx) => (
                          <tr key={row.id || rowIdx}>
                            {datasheetColumns.filter(c => c.visible !== false).map(col => {
                              const baseKey = col.fieldName.charAt(0).toLowerCase() + col.fieldName.slice(1);
                              const meta = fieldMetadata[col.fieldName];
                              let value = row[baseKey];

                              // Check for _Display suffix pattern first (backend convention)
                              const displaySuffixKey = baseKey + '_Display';
                              if (row[displaySuffixKey] !== undefined) {
                                value = row[displaySuffixKey];
                              } else if (meta?.fK_Display_Field) {
                                // Fallback to metadata FK display field
                                const displayKey = meta.fK_Display_Field.charAt(0).toLowerCase() + meta.fK_Display_Field.slice(1);
                                if (row[displayKey] !== undefined) {
                                  value = row[displayKey];
                                }
                              }

                              // Format dates based on UI_Type (utils/fieldDates)
                              if (meta?.uI_Type === 'date' && value) value = formatFieldDate(value, 'date');

                              return (
                                <td
                                  key={col.fieldName}
                                  style={{ textAlign: col.alignment || 'left' }}
                                >
                                  {typeof value === 'boolean' ? (value ? '✓' : '') : (value ?? '')}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ) : (
                        <tr className="lvc-preview-empty-row">
                          <td colSpan={datasheetColumns.filter(c => c.visible !== false).length}>
                            {tableHasApi(selectedTable) ? 'No data available' : 'Connect API to see sample data'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Section Modal */}
      {showSectionModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>Add Section</h3>
              <button type="button" className="stui-modal-close" onClick={() => setShowSectionModal(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="form-group">
                <label>Section Title</label>
                <input type="text" className="form-input" value={newSectionTitle} onChange={(e) => setNewSectionTitle(e.target.value)} placeholder="e.g. General Information" autoFocus />
              </div>
              <div className="form-group">
                <label>Insert at Row</label>
                <input type="number" className="form-input" min="1" value={sectionTargetRow} onChange={(e) => setSectionTargetRow(parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowSectionModal(false)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddSection} disabled={!newSectionTitle.trim()}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Derived Field Modal */}
      {showDerivedModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>Add Derived Field</h3>
              <button type="button" className="stui-modal-close" onClick={() => { setShowDerivedModal(false); setDerivedTargetFields([]); setNewDerivedSourceField(''); setNewDerivedTargetField(''); setNewDerivedLabel(''); }}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="form-hint stui-mb-md">
                Derived fields display a read-only value from a related record via an FK field in this table.
              </p>
              <div className="form-group">
                <label>Source FK Field <span className="stui-hint">(in this table)</span></label>
                <StuiSelect className="form-input" value={newDerivedSourceField} onChange={(e) => handleDerivedSourceChange(e.target.value)}>
                  <option value="">-- Select FK Field --</option>
                  {(moduleConfig?.schema?.fields || []).filter(f => f.fkTable).map(f => (
                    <option key={f.fieldName} value={f.fieldName}>{f.fieldName} → {f.fkTable}</option>
                  ))}
                </StuiSelect>
              </div>
              <div className="form-group">
                <label>Target Field <span className="stui-hint">(on related table)</span></label>
                <StuiSelect
                  className="form-input"
                  value={newDerivedTargetField}
                  onChange={(e) => setNewDerivedTargetField(e.target.value)}
                  disabled={!newDerivedSourceField || derivedTargetLoading}
                >
                  <option value="">
                    {derivedTargetLoading ? 'Loading...' : newDerivedSourceField ? '-- Select Field --' : '-- Select source first --'}
                  </option>
                  {derivedTargetFields.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </StuiSelect>
              </div>
              <div className="form-group">
                <label>Label</label>
                <input type="text" className="form-input" value={newDerivedLabel} onChange={(e) => setNewDerivedLabel(e.target.value)} placeholder="e.g. Manufacturer" />
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => { setShowDerivedModal(false); setDerivedTargetFields([]); setNewDerivedSourceField(''); setNewDerivedTargetField(''); setNewDerivedLabel(''); }}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddDerivedField} disabled={!newDerivedSourceField || !newDerivedTargetField}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* List Panel Config Modal */}
      {showListConfigModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>List View Search Configuration</h3>
              <button type="button" className="stui-modal-close" onClick={() => setShowListConfigModal(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="modal-description">Configure how records appear in the list panel search results.</p>
              <div className="form-group">
                <label>Primary Line</label>
                <StuiSelect className="form-input" value={headerSettings.primaryLine} onChange={(e) => handleHeaderChange('primaryLine', e.target.value)}>
                  <option value="">-- Select Field --</option>
                  {getAllFieldNames().map(f => <option key={f} value={f}>{f}</option>)}
                </StuiSelect>
                <span className="form-hint">Main text shown for each record</span>
              </div>
              <div className="form-group">
                <label>Secondary Line</label>
                <StuiSelect className="form-input" value={headerSettings.secondaryLine} onChange={(e) => handleHeaderChange('secondaryLine', e.target.value)}>
                  <option value="">-- Select Field --</option>
                  {getAllFieldNames().map(f => <option key={f} value={f}>{f}</option>)}
                </StuiSelect>
                <span className="form-hint">Additional info shown below primary</span>
              </div>
              <div className="form-row-2col">
                <div className="form-group">
                  <label>Sort Field</label>
                  <StuiSelect className="form-input" value={headerSettings.sortField} onChange={(e) => handleHeaderChange('sortField', e.target.value)}>
                    <option value="">-- Select Field --</option>
                    {getAllFieldNames().map(f => <option key={f} value={f}>{f}</option>)}
                  </StuiSelect>
                </div>
                <div className="form-group">
                  <label>Sort Direction</label>
                  <StuiSelect className="form-input" value={headerSettings.sortDirection} onChange={(e) => handleHeaderChange('sortDirection', e.target.value)}>
                    <option value="ASC">Ascending</option>
                    <option value="DESC">Descending</option>
                  </StuiSelect>
                </div>
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-primary" onClick={() => setShowListConfigModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Property Editor Modal */}
      {editingField && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Edit: {editingField.fieldName}</h3>
              <button type="button" className="stui-modal-close" onClick={() => setEditingField(null)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              {editingField.fieldType !== 'spacer' && (
                <div className="form-group">
                  <label>Label</label>
                  <input type="text" className="form-input" value={editingField.label} onChange={(e) => setEditingField({ ...editingField, label: e.target.value })} />
                </div>
              )}
              <div className="form-row-2col">
                <div className="form-group">
                  <label>Col Span (1-24)</label>
                  <input type="number" className="form-input" min="1" max="24" value={editingField.colSpan} onChange={(e) => setEditingField({ ...editingField, colSpan: parseInt(e.target.value) || 8 })} />
                </div>
                {editingField.fieldType !== 'spacer' && (
                  <>
                    <div className="form-group">
                      <label>Label Position</label>
                      <StuiSelect className="form-input" value={editingField.labelPosition} onChange={(e) => setEditingField({ ...editingField, labelPosition: e.target.value })}>
                        <option value="top">Top</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="bottom">Bottom</option>
                      </StuiSelect>
                    </div>
                    <div className="form-group">
                      <label>Label Alignment</label>
                      <StuiSelect className="form-input" value={editingField.labelAlignment || 'left'} onChange={(e) => setEditingField({ ...editingField, labelAlignment: e.target.value })}>
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </StuiSelect>
                    </div>
                  </>
                )}
              </div>
              {editingField.fieldType !== 'spacer' && (fieldMetadata[editingField.fieldName]?.uI_Type?.toLowerCase() === 'textarea' || suggestFieldType(editingField.dataType) === 'textarea') && (
                <div className="form-group">
                  <label>Textarea Rows</label>
                  <input
                    type="number"
                    className="form-input"
                    min="2"
                    max="30"
                    value={editingField.textareaRows || 3}
                    onChange={(e) => setEditingField({ ...editingField, textareaRows: parseInt(e.target.value) || 3 })}
                  />
                  <span className="stui-hint">Default: 3 rows</span>
                </div>
              )}
              {editingField.fieldType !== 'spacer' && (editingField.fieldType === 'fk-derived' ? (
                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Source FK Field</label>
                    <input type="text" className="form-input" value={editingField.sourceField || ''} onChange={(e) => setEditingField({ ...editingField, sourceField: e.target.value })} placeholder="e.g. Index_Sheet" />
                    <span className="form-hint">FK field in this table</span>
                  </div>
                  <div className="form-group">
                    <label>Target Field</label>
                    <input type="text" className="form-input" value={editingField.targetField || ''} onChange={(e) => setEditingField({ ...editingField, targetField: e.target.value })} placeholder="e.g. Manufacturer" />
                    <span className="form-hint">Field on related table</span>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label>UI Type <span className="stui-hint">(from Field Manager)</span></label>
                  <input
                    type="text"
                    className="form-input"
                    value={fieldMetadata[editingField.fieldName]?.uI_Type || editingField.fieldType || 'text'}
                    readOnly
                  />
                </div>
              ))}
              {/* FK Settings - Read-only from Field Manager */}
              {editingField.fieldType !== 'spacer' && fieldMetadata[editingField.fieldName]?.fK_Table && (
                <>
                  <div className="form-row-3col">
                    <div className="form-group">
                      <label>FK Table</label>
                      <input
                        type="text"
                        className="form-input"
                        value={fieldMetadata[editingField.fieldName]?.fK_Table || ''}
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label>FK Display Field</label>
                      <input
                        type="text"
                        className="form-input"
                        value={fieldMetadata[editingField.fieldName]?.fK_Display_Field || ''}
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label>Clickable</label>
                      <input
                        type="text"
                        className="form-input"
                        value={fieldMetadata[editingField.fieldName]?.fK_Clickable !== false ? 'Yes' : 'No'}
                        readOnly
                      />
                    </div>
                  </div>
                  <p className="form-hint">FK settings are managed in Field Manager</p>
                </>
              )}
              {/* Label Source Settings - Read-only from Field Manager */}
              {editingField.fieldType !== 'spacer' && fieldMetadata[editingField.fieldName]?.label_Source_Table && (
                <>
                  <div className="form-row-3col">
                    <div className="form-group">
                      <label>Label Source Table</label>
                      <input
                        type="text"
                        className="form-input"
                        value={fieldMetadata[editingField.fieldName]?.label_Source_Table || ''}
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label>Label Source Column</label>
                      <input
                        type="text"
                        className="form-input"
                        value={fieldMetadata[editingField.fieldName]?.label_Source_Column || ''}
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label>Label Source FK</label>
                      <input
                        type="text"
                        className="form-input"
                        value={fieldMetadata[editingField.fieldName]?.label_Source_FK || ''}
                        readOnly
                      />
                    </div>
                  </div>
                  <p className="form-hint">Label source settings are managed in Field Manager</p>
                </>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setEditingField(null)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleSaveFieldProperties}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* No API Warning Modal */}
      {showNoApiModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>No API Found for {selectedTable}</h3>
              <button type="button" className="stui-modal-close" onClick={() => { setShowNoApiModal(false); setApiTasksHandled(true); }}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="modal-warning">
                This table does not have a backend API controller yet. The list view module will not function until the API is created.
              </p>
              <p>If you save this config, the following tasks will be added to the Task Log:</p>
              <ul className="modal-task-list">
                <li><strong>Create API: {selectedTable}</strong> - Build CRUD endpoints</li>
                <li><strong>Test API: {selectedTable}</strong> - Verify endpoints work</li>
                <li><strong>Build Module: {selectedTable}</strong> - Create the list view page</li>
              </ul>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => { setShowNoApiModal(false); setApiTasksHandled(true); }}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={() => { setApiTasksHandled(true); doSave(true); }} disabled={saving}>
                {saving ? 'Saving...' : 'Save & Create Tasks'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Related Table Tab Editor Modal */}
      {showRelatedTabModal && editingRelatedTab && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>{editingRelatedTab.isNew ? 'Add Related Table Tab' : 'Edit Related Table Tab'}</h3>
              <button type="button" className="stui-modal-close" onClick={() => { setShowRelatedTabModal(false); setEditingRelatedTab(null); }}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="modal-description">
                Configure a tab to show related records from a table that references {selectedTable}.
              </p>

              <div className="form-row-2col">
                <div className="form-group">
                  <label>Related Table</label>
                  <StuiSelect
                    className="form-input"
                    value={editingRelatedTab.tableName}
                    onChange={(e) => handleRelatedTabTableChange(e.target.value)}
                  >
                    <option value="">-- Select Table --</option>
                    {getCandidateRelatedTables().map(c => (
                      <option key={c.tableName} value={c.tableName}>
                        {c.tableName} (via {c.fkField})
                      </option>
                    ))}
                  </StuiSelect>
                  <span className="form-hint">Tables with FK pointing to {selectedTable}</span>
                </div>
                <div className="form-group">
                  <label>Tab Label</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingRelatedTab.label}
                    onChange={(e) => setEditingRelatedTab({ ...editingRelatedTab, label: e.target.value })}
                    placeholder={editingRelatedTab.tableName ? formatLabel(editingRelatedTab.tableName) : 'Tab label'}
                  />
                  <span className="form-hint">Display name shown on tab</span>
                </div>
              </div>

              {editingRelatedTab.tableName && (
                <>
                  <div className="form-group">
                    <label>FK Field</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editingRelatedTab.fkField}
                      disabled
                    />
                    <span className="form-hint">Column in {editingRelatedTab.tableName} that links to {selectedTable}</span>
                  </div>

                  <div className="form-group">
                    <label>Columns to Display</label>
                    <div className="dual-listbox">
                      <div className="dual-listbox-column">
                        <label>Available</label>
                        <div className="dual-listbox-list">
                          {getTableColumns(editingRelatedTab.tableName)
                            .filter(col => col !== editingRelatedTab.fkField)
                            .filter(col => !editingRelatedTab.columns?.some(c => getColName(c) === col))
                            .map(col => (
                              <div
                                key={col}
                                className={`dual-listbox-item ${selectedAvailable === col ? 'selected' : ''}`}
                                onClick={() => setSelectedAvailable(col)}
                                onDoubleClick={() => { setSelectedAvailable(col); handleAddColumn(); }}
                              >
                                {col}
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="dual-listbox-controls">
                        <button
                          type="button"
                          onClick={handleAddColumn}
                          disabled={!selectedAvailable}
                          title="Add column"
                        >
                          <ChevronRight size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveColumn}
                          disabled={!selectedChosen}
                          title="Remove column"
                        >
                          <ChevronLeft size={16} />
                        </button>
                      </div>
                      <div className="dual-listbox-column">
                        <label>Selected (in order)</label>
                        <div className="dual-listbox-list">
                          {(editingRelatedTab.columns || []).map(col => {
                            const colName = getColName(col);
                            return (
                              <div
                                key={colName}
                                className={`dual-listbox-item ${selectedChosen === colName ? 'selected' : ''}`}
                                onClick={() => setSelectedChosen(colName)}
                                onDoubleClick={() => { setSelectedChosen(colName); handleRemoveColumn(); }}
                              >
                                {colName}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="dual-listbox-order">
                        <button
                          type="button"
                          onClick={handleMoveColumnUp}
                          disabled={!selectedChosen || (editingRelatedTab.columns || []).findIndex(c => getColName(c) === selectedChosen) === 0}
                          title="Move up"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={handleMoveColumnDown}
                          disabled={!selectedChosen || (editingRelatedTab.columns || []).findIndex(c => getColName(c) === selectedChosen) >= (editingRelatedTab.columns || []).length - 1}
                          title="Move down"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </div>
                    <span className="form-hint">Double-click to add/remove. Use arrows to reorder selected columns.</span>
                  </div>

                  <div className="form-row-2col">
                    <div className="form-group">
                      <label>Sort Field</label>
                      <StuiSelect
                        className="form-input"
                        value={editingRelatedTab.sortField || ''}
                        onChange={(e) => setEditingRelatedTab({ ...editingRelatedTab, sortField: e.target.value })}
                      >
                        <option value="">-- Default --</option>
                        {editingRelatedTab.columns?.map(col => {
                          const colName = typeof col === 'string' ? col : col.fieldName;
                          return <option key={colName} value={colName}>{colName}</option>;
                        })}
                      </StuiSelect>
                    </div>
                    <div className="form-group">
                      <label>Sort Direction</label>
                      <StuiSelect
                        className="form-input"
                        value={editingRelatedTab.sortDirection || 'ASC'}
                        onChange={(e) => setEditingRelatedTab({ ...editingRelatedTab, sortDirection: e.target.value })}
                      >
                        <option value="ASC">Ascending</option>
                        <option value="DESC">Descending</option>
                      </StuiSelect>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => { setShowRelatedTabModal(false); setEditingRelatedTab(null); }}>Cancel</button>
              <button
                className="stui-btn stui-btn-primary"
                onClick={handleSaveRelatedTab}
                disabled={!editingRelatedTab.tableName || !editingRelatedTab.columns?.length}
              >
                {editingRelatedTab.isNew ? 'Add Tab' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListViewConfigPage;
